import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { tenantsApi, tenantUsersApi } from '../../services/api';
import { PlusIcon, EditIcon, TrashIcon, EyeIcon, EyeOffIcon } from '../../utils/icons';
import { formatDate } from '../../utils/formatters';
import Modal from '../../components/Modal';
import './TenantUsers.css';

export default function TenantUsers() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError('');
      const [tenantRes, usersRes] = await Promise.all([
        tenantsApi.get(tenantId),
        tenantUsersApi.list(tenantId),
      ]);
      setTenant(tenantRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Organisation not found.');
      } else {
        setError('Failed to load data.');
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeCount = users.filter((u) => u.is_active).length;
  const atLimit = tenant ? activeCount >= tenant.max_users : false;

  const handleCreated = () => {
    setShowCreate(false);
    fetchData();
  };

  const handleUpdated = () => {
    setEditingUser(null);
    fetchData();
  };

  const handleDeleted = () => {
    setDeletingUser(null);
    fetchData();
  };

  if (loading) {
    return (
      <div className="tu-page">
        <div className="tu-loading">
          <div className="tu-loading-spinner" />
          <p>Loading users…</p>
        </div>
      </div>
    );
  }

  if (error && !tenant) {
    return (
      <div className="tu-page">
        <div className="tu-error-page">
          <p>{error}</p>
          <button className="btn btn-ghost" onClick={() => navigate('/super-admin/tenants')}>
            Back to Organisations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tu-page">
      {/* Breadcrumb */}
      <nav className="tu-breadcrumb animate-fade-up">
        <Link to="/super-admin/tenants" className="tu-breadcrumb-link">Organisations</Link>
        <span className="tu-breadcrumb-sep">/</span>
        <Link to={`/super-admin/tenants/${tenantId}`} className="tu-breadcrumb-link">{tenant.name}</Link>
        <span className="tu-breadcrumb-sep">/</span>
        <span className="tu-breadcrumb-current">Users</span>
      </nav>

      {/* Header */}
      <div className="page-header animate-fade-up" style={{ animationDelay: '40ms' }}>
        <div className="page-header-top">
          <div>
            <p className="section-label" style={{ marginBottom: 4 }}>User Management</p>
            <h1 className="page-title">{tenant.name}</h1>
            <p className="page-subtitle">{users.length} user{users.length !== 1 ? 's' : ''} in this organisation</p>
          </div>
          <div className="page-actions">
            <button
              className="btn btn-primary"
              onClick={() => setShowCreate(true)}
              disabled={atLimit}
              title={atLimit ? `User limit reached (${tenant.max_users})` : 'Add a new user'}
            >
              <PlusIcon /> Add User
            </button>
          </div>
        </div>
      </div>

      {error && <div className="tu-error animate-fade-up">{error}</div>}

      {/* User limit bar */}
      <div className="tu-limit-card card-sm animate-fade-up" style={{ animationDelay: '60ms' }}>
        <div className="tu-limit-header">
          <span className="tu-limit-text">
            <strong>{activeCount}</strong> of <strong>{tenant.max_users}</strong> user slots used
          </span>
          {atLimit && <span className="tu-limit-warning">Limit reached</span>}
        </div>
        <div className="tu-limit-track">
          <div
            className={`tu-limit-fill ${atLimit ? 'tu-limit-fill--full' : ''}`}
            style={{ width: `${Math.min((activeCount / tenant.max_users) * 100, 100)}%` }}
          />
        </div>
      </div>

      {/* Users table */}
      <div className="animate-fade-up" style={{ animationDelay: '80ms' }}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="tu-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="tu-empty">
                    No users yet. Create the first admin user for this organisation.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="tu-name-cell">
                        <div className="tu-avatar">
                          {u.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                        </div>
                        <span className="tu-name">{u.name}</span>
                      </div>
                    </td>
                    <td className="tu-email">{u.email}</td>
                    <td>
                      <span className={`tu-role-badge tu-role-badge--${u.role}`}>
                        {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${u.is_active ? 'closed' : 'lost'}`}>
                        <span className="badge-dot" />
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="tu-date">
                      {formatDate(u.created_at)}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button className="btn-icon" title="Edit" onClick={() => setEditingUser(u)}>
                          <EditIcon />
                        </button>
                        {u.is_active && (
                          <button className="btn-icon btn-icon--danger" title="Deactivate" onClick={() => setDeletingUser(u)}>
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create user modal */}
      {showCreate && (
        <UserFormModal
          title="Add User"
          tenantId={tenantId}
          onClose={() => setShowCreate(false)}
          onSaved={handleCreated}
        />
      )}

      {/* Edit user modal */}
      {editingUser && (
        <UserFormModal
          title="Edit User"
          tenantId={tenantId}
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={handleUpdated}
        />
      )}

      {/* Delete confirmation */}
      {deletingUser && (
        <DeleteUserModal
          user={deletingUser}
          tenantId={tenantId}
          onClose={() => setDeletingUser(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
}


/* ─── User Form Modal ────────────────────────────────────────────────────── */

function UserFormModal({ title, tenantId, user, onClose, onSaved }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'admin',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        const updates = {};
        if (form.name !== user.name) updates.name = form.name;
        if (form.email !== user.email) updates.email = form.email;
        if (form.role !== user.role) updates.role = form.role;
        if (form.password) updates.password = form.password;
        await tenantUsersApi.update(tenantId, user.id, updates);
      } else {
        await tenantUsersApi.create(tenantId, form);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={title} size="sm" onClose={onClose}>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="modal-error">{error}</div>}

            <div className="form-group">
              <label className="section-label">Name *</label>
              <input
                className="input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Full name"
                required
              />
            </div>

            <div className="form-group">
              <label className="section-label">Email *</label>
              <input
                className="input"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="user@org.com"
                required
              />
            </div>

            <div className="form-group">
              <label className="section-label">
                Password {isEdit ? '(leave blank to keep current)' : '*'}
              </label>
              <div className="tu-password-wrap">
                <input
                  className="input"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder={isEdit ? '••••••••' : 'Min 6 characters'}
                  required={!isEdit}
                  minLength={form.password ? 6 : undefined}
                />
                <button
                  type="button"
                  className="tu-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="section-label">Role *</label>
              <select
                className="input tu-select"
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              >
                <option value="admin">Admin</option>
                <option value="agent">Agent</option>
              </select>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create User'}
            </button>
          </div>
        </form>
    </Modal>
  );
}


/* ─── Delete User Modal ──────────────────────────────────────────────────── */

function DeleteUserModal({ user, tenantId, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setDeleting(true);
    setError('');
    try {
      await tenantUsersApi.delete(tenantId, user.id);
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to deactivate.');
      setDeleting(false);
    }
  };

  return (
    <Modal title="Deactivate User" size="sm" onClose={onClose}>
        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}
          <p className="delete-msg">
            Are you sure you want to deactivate <strong>{user.name}</strong>?
          </p>
          <p className="delete-hint">
            This user will no longer be able to log in. Their data will be preserved.
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-danger" onClick={handleConfirm} disabled={deleting}>
            {deleting ? 'Deactivating…' : 'Deactivate'}
          </button>
        </div>
    </Modal>
  );
}


